import { X } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAddCredits } from '@/hooks/useUsers';

export default function CreditsModal({
  isOpen,
  onClose,
  userId,
}: {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
}) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const addCredits = useAddCredits();
  const toast = useToast();

  if (!isOpen) return null;

  const handleClose = () => {
    setAmount('');
    setDescription('');
    onClose();
  };

  const handleSubmit = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents <= 0)
      return toast.error('Invalid amount', 'Enter a positive amount');
    try {
      const res = await addCredits.mutateAsync({
        userId,
        amount_cents: cents,
        description: description || undefined,
      });
      toast.success('Credits added', res.message);
      handleClose();
    } catch (e) {
      toast.error(
        'Failed to add credits',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Add Stripe invoice credit</CardTitle>
              <CardDescription>
                Adds Stripe credit balance to this user's account; applies to
                their next invoice.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Amount (USD)
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="10.00"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Reason for credit"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={addCredits.isPending || !amount}
              className="flex-1"
            >
              {addCredits.isPending ? 'Adding...' : 'Add Credits'}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
